import { NgIf, AsyncPipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { CartLineSources, LocationSilo } from '@next/core-lib/cart';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { Store } from '@ngrx/store';

import { Observable, Subject, takeUntil } from 'rxjs';

import * as ProductActions from 'src/app/product/store/actions';
import * as ProductSelectors from 'src/app/product/store/selectors';
import { deleteSiloRequest, openEditSiloDialog } from 'src/app/store/actions';

import { LocationSiloWithProductList } from '../../models/silo-product-list.models';

import { SiloProductListComponent } from '../silo-product-list/silo-product-list.component';

@Component({
  selector: 'app-silos-table',
  templateUrl: './silos-table.component.html',
  styleUrls: ['./silos-table.component.scss'],
  imports: [
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatButton,
    MatIcon,
    NgIf,
    MatIconButton,
    SiloProductListComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AsyncPipe,
    AgrI18nSharedModule,
  ],
})
export class SilosTableComponent implements OnInit, OnDestroy {
  @ViewChild('openSiloAddProductsButton') openSiloAddProductsButton: MatButton;
  public dataSource$: Observable<LocationSiloWithProductList[]>;
  public columnsToDisplay: string[] = ['siloNumber', 'actions'];
  public columnsToDisplayWithExpand: string[] = ['siloNumber', 'name', 'actions', 'expand'];
  public expandedSiloMap: Record<number, boolean> = {};
  public expandedSiloIds$: Observable<number[]>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store,
    @Inject(BreakpointServiceInjector) readonly breakpointService: BreakpointService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.dataSource$ = this.store.select(ProductSelectors.selectSilosWithProductLists).pipe(takeUntil(this.destroy$));
    this.expandedSiloIds$ = this.store.select(ProductSelectors.selectExpandedSiloIds).pipe(takeUntil(this.destroy$));

    this.expandedSiloIds$.subscribe((expandedSiloIds) => {
      this.expandedSiloMap = expandedSiloIds.reduce(
        (acc, id) => {
          acc[id] = true;
          return acc;
        },
        {} as Record<number, boolean>,
      );
    });
  }

  toggleExpand(siloId: number): void {
    const updatedSiloIds = this.expandedSiloMap[siloId]
      ? Object.keys(this.expandedSiloMap)
          .map(Number)
          .filter((id) => id !== siloId)
      : [...Object.keys(this.expandedSiloMap).map(Number), siloId];

    this.store.dispatch(ProductActions.setExpandedSiloIds({ siloIds: updatedSiloIds }));
  }

  deleteSilo(silo: LocationSilo): void {
    this.store.dispatch(deleteSiloRequest({ silo }));
  }

  editSilo(silo: LocationSilo): void {
    this.store.dispatch(openEditSiloDialog({ silo }));
  }

  openProductDialog(silo: LocationSiloWithProductList) {
    this.store.dispatch(
      ProductActions.openSearchDialog({
        silo,
        htmlButtonRef: this.openSiloAddProductsButton._elementRef.nativeElement as HTMLButtonElement,
        source: CartLineSources.SILO_PRODUCT_LIST,
      }),
    );
  }
}
